import * as React from "react";
import {cva, type VariantProps} from "class-variance-authority";
import {Slot} from "@radix-ui/react-slot";

import {cn} from "@/utils/utils";
import {LoaderButton} from "@videohighlight/ui/loaders";

const buttonVariants = cva(
    "inline-flex items-center justify-center rounded-md shadow focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-sm text-primary-foreground shadow hover:opacity-90 font-regular',
                danger:
                    'bg-danger text-danger-foreground shadow-sm hover:bg-danger hover:opacity-90',
                destructive:
                    'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive hover:opacity-90',
                ghost: 'shadow-none text-muted-foreground ' +
                    'hover:bg-accent hover:text-accent-foreground hover:shadow-sm',
                ghostBackdrop: 'shadow-none text-muted-foreground ' +
                    'hover:bg-backdropAccent hover:text-backdropAccentForeground hover:shadow-sm',
                ghostWithBorder: 'shadow-none text-muted-foreground border border-transparent border-0.5 ' +
                    'hover:bg-secondary hover:text-accent-foreground hover:shadow-sm hover:border-border-contrast hover:border-0.5',
                // ghostWithMediumBorder: 'shadow-none text-muted-foreground border border-transparent border-1 ' +
                //     'hover:bg-secondary hover:text-accent-foreground hover:shadow-sm hover:border hover:border-border hover:border-1',
                ghostWithBorderHovered: 'bg-secondary text-accent-foreground shadow-sm border border-border border-0.5',
                ghostOverlay: 'shadow-none text-muted-foreground ' +
                    'hover:bg-secondary hover:text-accent-foreground hover:shadow-sm hover:border ' +
                    'group-hover:bg-secondary group-hover:text-accent-foreground ',
                homepage: 'bg-gradient-to-br from-vh-pink from-10% via-vh-light-blue via-25% to-vh-dark-blue to-65% ' +
                    'text-primary-foreground shadow hover:opacity-90 font-regular',
                homepageSimple: 'bg-vh-dark-blue ' +
                    'text-primary-foreground shadow hover:opacity-90 font-regular',
                pricing: 'bg-vh-dark-blue ' +
                    'text-primary-foreground shadow font-regular ' +
                    'hover:opacity-90 ',
                icon: 'shadow-none ' +
                    'hover:text-accent-foreground',
                outline: 'bg-transparent border text-muted-foreground shadow-sm ' +
                    'hover:bg-secondary hover:text-accent-foreground hover:border hover:border-border',
                outlineNoBorder: 'bg-transparent border border-transparent text-muted-foreground shadow-sm ' +
                    'hover:bg-secondary hover:text-accent-foreground hover:border hover:border-border',
                link: 'text-primary underline-offset-4 shadow-none hover:underline',
                linkSimple: 'text-primary underline-offset-4 shadow-none',
                linkText: 'text-primary underline-offset-4 shadow-none px-0 pointer-arrow',
                premium: "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white border-0",
                premiumSlator: "bg-gradient-to-r from-[#7cc89a] via-[#57c9e9] to-[#0098d6] text-white border-0",
                secondary:
                    'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary hover:opacity-90 ',
                timestamp: 'shadow-none font-regular text-base rounded text-muted-foreground outline-none' +
                    'focus:bg-primary hover:bg-primary ' +
                    'focus:text-primary-foreground hover:text-primary-foreground',
                timestampNew: 'shadow-none font-regular text-base rounded text-secondary-foreground outline-none' +
                    'focus:bg-secondary hover:bg-secondary ' +
                    'focus:text-secondary-foreground hover:text-secondary-foreground',
            },
            size: {
                default: 'h-9 px-4 py-2',
                sm: 'h-8 rounded-md px-3 text-sm',
                lg: 'h-11 rounded-md px-4',
                'homepage-cta': 'h-10 rounded-[10px] px-4',
                xl: 'h-12 rounded-xl px-6',
                icon: 'h-8 w-8 p-0',
                iconSmall: 'h-4 w-4 p-0',
                timestamp: 'h-7 rounded-md px-2',
                link: 'px-0'
            },
            slant: {
                none: '',
                start: 'relative slanted-start',
                end: 'relative slanted-end',
                both: 'relative slanted-both'
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
            slant: 'none'
        }
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean
    isSubmitting?: boolean
    isAnimated?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({className, variant, size, asChild = false, isSubmitting = false, isAnimated, ...props}, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                className={
                    cn(
                        buttonVariants({variant, size, className}),
                        (isAnimated && "animate-pulse transition-all duration-2000")
                    )
                }
                ref={ref}
                disabled={isSubmitting}
                {...props}
            >
                {isSubmitting && <LoaderButton/>}
                {props.children}
            </Comp>
        )
    }
);
Button.displayName = "Button";

export {Button, buttonVariants}
